"use client";

import { useEffect, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { COPI_HELP_EXAMPLES, type CopiExampleKey } from "@/data/copi-help-examples";
import Link from "next/link";
import {
  Home, Music2, Users, ShoppingBag, MessageSquare, Play,
  Mic, Radio, Newspaper, Ticket, BarChartBig, Search,
  Sparkles, BookOpen, ChevronRight, HelpCircle, UserCircle2,
  Upload, LayoutGrid, Headphones, Film, DollarSign, Shield,
  ChevronDown, ChevronUp, Heart, Globe, Layers, Settings2,
  Wand2, Star, Volume2, Repeat2, Shuffle, Download, Lock,
  QrCode, Eye, Palette, Scale, TrendingUp, FileText,
  Zap, Mic2, Video, Clock, Users2, Plus, Edit3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { RightsAndFees } from "@/components/rights-and-fees";
import { HELP_SECTIONS, type HelpSection } from "@/data/help-sections";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Step {
  title: string;
  detail: string;
}

interface Feature {
  name: string;
  summary: string;
  steps: Step[];
  imageUrl?: string;
  videoId?: string;
  userTypes?: string[];
  badge?: string;
}

interface Section {
  id: string;
  icon: React.ElementType;
  title: string;
  color: string;
  bg: string;
  intro: string;
  features: Feature[];
}

// ── Content ───────────────────────────────────────────────────────────────────

// The guide's text lives in src/data/help-sections.ts (translated per language).
const HELP_ICONS: Record<string, React.ElementType> = { FileText, Heart, Home, LayoutGrid, MessageSquare, Mic, Music2, Play, Radio, Scale, Search, ShoppingBag, Ticket, Users };

// ── Component ─────────────────────────────────────────────────────────────────

export default function HelpPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const tc = useTranslations("helpCopi");
  const tp = useTranslations("helpPage");
  const locale = useLocale();
  // The guide in the reader's language — translated once on the server and kept.
  const [translated, setTranslated] = useState<HelpSection[] | null>(null);
  useEffect(() => {
    if (locale === "en") { setTranslated(null); return; }
    let live = true;
    fetch(`/api/help/translate?locale=${encodeURIComponent(locale)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (live && Array.isArray(d?.sections)) setTranslated(d.sections); })
      .catch(() => {});
    return () => { live = false; };
  }, [locale]);
  const sections = useMemo<Section[]>(() => (translated ?? HELP_SECTIONS).map(sec => ({ ...sec, icon: HELP_ICONS[sec.icon] ?? HelpCircle })), [translated]);
  // coπ first (Gordon 2026-11-02: "add a coπ tutorial in the help page").
  // Descriptions are translated; the spoken examples are hand-written for the
  // four languages coπ understands, English everywhere else.
  const allSections = useMemo<Section[]>(() => {
    const ex = COPI_HELP_EXAMPLES[(["sv", "es", "fr"].includes(locale) ? locale : "en") as "en" | "sv" | "es" | "fr"];
    const step = (title: string, key: CopiExampleKey, desc: string): Step => ({
      title, detail: `${ex[key].map(e => `“${e}”`).join(" · ")} — ${desc}`,
    });
    const copi: Section = {
      id: "copi", icon: Mic, title: tc("title"), color: "text-cyan-500", bg: "bg-cyan-500/10", intro: tc("intro"),
      features: [
        { name: tc("showName"), summary: tc("showSummary"), steps: [
          step(tc("pagesTitle"), "pages", tc("pagesDesc")),
          step(tc("peopleTitle"), "people", tc("peopleDesc")),
          step(tc("shopTitle"), "shop", tc("shopDesc")),
          step(tc("namesTitle"), "names", tc("namesDesc")),
          step(tc("mineTitle"), "mine", tc("mineDesc")),
        ] },
        { name: tc("playName"), summary: tc("playSummary"), steps: [
          step(tc("playNameTitle"), "playName", tc("playNameDesc")),
          step(tc("playStyleTitle"), "playStyle", tc("playStyleDesc")),
          step(tc("controlTitle"), "control", tc("controlDesc")),
        ] },
        { name: tc("doName"), summary: tc("doSummary"), steps: [
          step(tc("learnTitle"), "learn", tc("learnDesc")),
          step(tc("toolsTitle"), "tools", tc("toolsDesc")),
        ] },
        { name: tc("unsureName"), summary: tc("unsureSummary"), steps: [{ title: tc("unsureTitle"), detail: tc("unsureDesc") }] },
      ],
    };
    return [copi, ...sections];
  }, [tc, locale, sections]);

  const [activeTopic, setActiveTopic] = useState<string | null>(null);

  function toggle(key: string) {
    setExpanded((prev) => (prev === key ? null : key));
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ── Sticky header ── */}
      <div className="sticky top-[var(--header-height)] z-30 bg-background/95 backdrop-blur-md border-b border-foreground/20">
        <div className="px-page py-5 sm:py-6 space-y-4 bg-gradient-to-br from-emerald-500/10 via-transparent to-amber-500/5">

          {/* Title */}
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/40 shrink-0">
              <HelpCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight">
                {tp("title")}
              </h1>
              <p className="text-muted-foreground text-xs sm:text-sm leading-snug">
                {tp("subtitle")}
              </p>
            </div>
          </div>

          {/* Section nav pills — ONE horizontally-scrollable row on mobile
              (the wrapped block ate half the screen; Gordon: "header with
              filters taking a lot of space in mobile"); wraps on desktop. */}
          <div aria-label={tp("topics")} className="flex gap-2 overflow-x-auto hide-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
            {allSections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={() => setActiveTopic(s.id)}
                aria-current={activeTopic === s.id ? "location" : undefined}
                className={cn("inline-flex min-h-10 items-center gap-2 px-3.5 py-2 rounded-lg border text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  activeTopic === s.id ? "border-foreground bg-foreground text-background" : "border-foreground/50 bg-card text-foreground hover:border-foreground hover:bg-foreground/10")}
              >
                <s.icon className={cn("h-4 w-4 flex-shrink-0", activeTopic === s.id ? "text-background" : s.color)} />
                {s.title}
              </a>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{tp.rich("helpMode", { b: (c) => <strong className="text-foreground">{c}</strong> })}</p>
        </div>
      </div>

      {/* ── Sections ── */}
      <div className="px-page py-8 space-y-10">
        {/* Rights & fees — what makers keep and what BB takes (5% for now). */}
        <div id="rights-and-fees" className="scroll-mt-[200px] sm:scroll-mt-[180px]">
          <RightsAndFees />
        </div>
        {allSections.map((section) => (
          <div key={section.id} id={section.id} className="scroll-mt-[200px] sm:scroll-mt-[180px]">

            {/* Section heading */}
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("p-2.5 rounded-lg shrink-0 border border-current/20", section.bg, section.color)}>
                <section.icon className={cn("h-5 w-5", section.color)} />
              </div>
              <div>
                <h2 className="text-lg font-bold">{section.title}</h2>
                <p className="text-xs text-muted-foreground">{section.intro}</p>
              </div>
            </div>

            {/* Feature accordion list */}
            <div className="space-y-2">
              {section.features.map((feat) => {
                const key = `${section.id}:${feat.name}`;
                const isOpen = expanded === key;
                return (
                  <div
                    key={feat.name}
                    className={cn(
                      "rounded-xl border bg-card overflow-hidden transition-all duration-200 cursor-pointer",
                      isOpen ? "border-foreground/50 shadow-sm" : "border-foreground/20 hover:border-foreground/50 hover:bg-muted/40"
                    )}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onClick={() => toggle(key)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(key); }
                    }}
                  >
                    {/* Accordion header */}
                    <div className="w-full text-left flex items-center gap-3 px-4 py-3">
                      <ChevronRight className={cn("h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200", isOpen && "rotate-90")} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{feat.name}</span>
                          {feat.badge && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                              {feat.badge}
                            </span>
                          )}
                          {feat.userTypes && feat.userTypes.map((t) => (
                            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium capitalize">
                              {t}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{feat.summary}</p>
                      </div>
                      {isOpen
                        ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                    </div>

                    {/* Expanded content */}
                    {isOpen && (
                      <div
                        className="px-4 pb-4 border-t border-border/40 pt-3 space-y-4"
                        onClick={e => e.stopPropagation()}
                      >

                        {/* Optional video */}
                        {feat.videoId && (
                          <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: "56.25%" }}>
                            <iframe
                              className="absolute inset-0 w-full h-full"
                              src={`https://www.youtube.com/embed/${feat.videoId}`}
                              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title={feat.name}
                            />
                          </div>
                        )}

                        {/* Optional image */}
                        {feat.imageUrl && !feat.videoId && (
                          <div className="rounded-lg overflow-hidden border border-border/40">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={feat.imageUrl} alt={feat.name} className="w-full object-cover" />
                          </div>
                        )}

                        {/* Step-by-step guide */}
                        <ol className="space-y-3">
                          {feat.steps.map((step, i) => (
                            <li key={i} className="flex gap-3">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm leading-tight">{step.title}</p>
                                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{step.detail}</p>
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* ── Still need help? ── */}
        <div className="rounded-xl border bg-muted/30 p-6 text-center space-y-3 mt-4">
          <Sparkles className="h-8 w-8 text-primary mx-auto" />
          <h2 className="text-lg font-bold">{tp("stillTitle")}</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {tp("stillBody")}
          </p>
          <Link
            href="/creator-ai"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-all"
          >
            <MessageSquare className="h-4 w-4" />
            {tp("stillCta")}
          </Link>
        </div>
      </div>
    </div>
  );
}
